import React, { useMemo } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  formatCurrency,
  formatTierPriceLines,
  getTierFeatures,
  getTieredPackageContentType,
  getTieredPackageSetColor,
  getResolvedTierPrices,
  TIER_LABELS,
  TIER_ORDER
} from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import { useQuote } from '../../../../context'
import TieredPackagePreviewCard from '../../../../options/TieredPackagePresetOptions/TieredPackagePreviewCard'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      /**
       * One rounded frame around all three packages, with the cards inset by 4px — the design's own
       * treatment. The cards keep their own borders, so the frame reads as a tray holding them
       * rather than a fourth border.
       */
      row: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 4,
        padding: 4,
        width: '100%',
        boxSizing: 'border-box',
        background: neutral.White,
        [theme.breakpoints.down('xs')]: {
          flexDirection: 'column'
        }
      },
      /**
       * The "Recommended" badge is absolutely placed and overhangs the top of its card, so every cell
       * reserves the room it needs. Dropped when the cards stack, where each has the gap above it.
       */
      cell: {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        paddingTop: 14,
        [theme.breakpoints.down('xs')]: {
          flex: '0 0 auto',
          paddingTop: 0
        }
      }
    }
  },
  { name: 'PackageComparisonCards' }
)

export interface PackageComparisonCardsProps {
  /**
   * `gate` — landing page before the estimate: emphasize + badge the recommended tier, CTA reads
   * "View Estimate" / "View <name>". `switch` — the "Compare Packages" dialog: emphasize the currently
   * selected tier with a "Selected" CTA, others read "Switch to <name>", no badge.
   */
  variant: 'gate' | 'switch'
  /** Called with the tier whose card was clicked. */
  onSelectTier: (tierId: TieredPackageTierId) => void
}

/**
 * The three-up package comparison the customer meets — on the gate before the estimate
 * ({@link TieredPackageGate}) and in the Compare Packages dialog ({@link PackageCompareDialog}).
 *
 * The card itself is {@link TieredPackagePreviewCard}, the same component the package settings and
 * the estimator's drawer preview render. That is deliberate: what the estimator styles in Settings —
 * accent colour, font, layout, price display, the badge and its label — is exactly what the customer
 * should see, and a second implementation of the card would drift from it. This component only
 * arranges those cards and decides what each button says.
 */
export default function PackageComparisonCards({ variant, onSelectTier }: PackageComparisonCardsProps) {
  const classes = useStyles({})
  const { quote } = useQuote()
  const { options } = useClientOptions()
  // Re-stamped before pricing, so the Compare dialog reads the estimate as it now stands. In the
  // customer view this resolves to the estimator's stamped prices unchanged (ENG-1795).
  const tierPrices = useMemo(() => getResolvedTierPrices({ quote, options }), [quote, options])
  const tieredPackages = quote.tieredPackages

  if (!tierPrices || !tieredPackages) {
    return null
  }

  const contentType = getTieredPackageContentType(tieredPackages)
  const setColor = getTieredPackageSetColor(tieredPackages)
  // The price shown on each card is the estimator's choice — total, difference, or both — so it is
  // resolved the same way the drawer's own preview resolves it.
  const prices = TIER_ORDER.reduce(
    (acc, tierId) => ({ ...acc, [tierId]: tierPrices[tierId]?.price ?? 0 }),
    {} as Partial<Record<TieredPackageTierId, number>>
  )

  // Switched-off packages are not offered anywhere the customer can see.
  const offered = TIER_ORDER.filter((tierId) => !tierPrices[tierId]?.isDisabled)

  return (
    <div className={classes.row}>
      {offered.map((tierId) => {
        const tier = tierPrices[tierId]
        const name = tier.name || TIER_LABELS[tierId]
        const lines = formatTierPriceLines({
          tierId,
          prices,
          priceDisplay: tieredPackages.priceDisplay,
          format: (value: number) => formatCurrency({ options, value })
        })

        const ctaText =
          variant === 'gate'
            ? tier.isRecommended
              ? undefined // the card's own default — "View <estimate>"
              : `View ${name}`
            : tier.isSelected
              ? 'Selected'
              : `Switch to ${name}`

        return (
          <div key={tierId} className={classes.cell}>
            <TieredPackagePreviewCard
              tier={tieredPackages.tiers[tierId]}
              fallbackName={TIER_LABELS[tierId]}
              features={getTierFeatures(tieredPackages, tierId)}
              contentType={contentType}
              // On the gate the badge marks what the estimator recommends. In the switch dialog it
              // marks where the customer already is, which is the more useful thing to point at.
              recommended={variant === 'gate' ? tier.isRecommended : tier.isSelected}
              badgeLabel={variant === 'gate' ? tieredPackages.badgeLabel : 'Selected'}
              color={setColor}
              font={tieredPackages.font}
              orientation={tieredPackages.layout?.orientation}
              pricePosition={tieredPackages.layout?.pricePosition}
              priceParts={lines.main}
              priceSubLine={lines.sub}
              priceSubInline={lines.subInline}
              ctaText={ctaText}
              onClick={() => onSelectTier(tierId)}
            />
          </div>
        )
      })}
    </div>
  )
}
